import { useSignIn } from '@clerk/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { EnvelopeSimple, LockSimple } from '@phosphor-icons/react';
import { Button, Field, Link, TextInput, toast } from '@reelplicate/ui';
import { Controller, useForm } from 'react-hook-form';
import { Navigate, useNavigate } from 'react-router';
import type { z } from 'zod';

import { AuthBody, AuthCard, AuthFooter, AuthHeader } from './auth-card';
import { describeClerkError } from './clerk-errors';
import { CodeField } from './code-field';
import { PasswordInput } from './password-input';
import {
  EmailCodeSchema,
  ForgotPasswordSchema,
  type ForgotPasswordValues,
  ResetPasswordSchema,
  type ResetPasswordValues,
} from './schemas';
import { BackToSignInButton, BackToSignInFooter, MailShortcuts } from './status-pages';

/*
 * Clerk resets passwords with an emailed six-digit code, all inside one sign-in
 * attempt: forgot-password (email) -> forgot-password/sent (code) ->
 * reset-password (new password) -> signed in.
 */

/** Figma "Auth B — Forgot password" (214:23158). */
export function ForgotPasswordPage() {
  const navigate = useNavigate();
  const { signIn } = useSignIn();
  const form = useForm<ForgotPasswordValues>({
    resolver: zodResolver(ForgotPasswordSchema),
    defaultValues: { email: '' },
  });

  const onSubmit = form.handleSubmit(async ({ email }) => {
    const created = await signIn.create({ identifier: email });
    if (created.error) {
      const { code, message } = describeClerkError(created.error);
      // Don't reveal whether the address has an account: carry on as if a
      // code went out. The code step will simply not accept anything.
      if (code === 'form_identifier_not_found') {
        await navigate('/forgot-password/sent');
        return;
      }
      form.setError('email', { message }, { shouldFocus: true });
      return;
    }
    const sent = await signIn.resetPasswordEmailCode.sendCode();
    if (sent.error) {
      form.setError('email', { message: describeClerkError(sent.error).message });
      return;
    }
    await navigate('/forgot-password/sent');
  });

  return (
    <AuthCard>
      <AuthHeader
        icon={{ icon: LockSimple }}
        title="Forgot your password?"
        subtitle="We’ll email you a code to choose a new one"
      />
      <form onSubmit={onSubmit} noValidate>
        <AuthBody>
          <Controller
            name="email"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field label="Email" error={fieldState.error?.message}>
                <TextInput
                  {...field}
                  type="email"
                  autoComplete="email"
                  placeholder="you@example.com"
                  leadingIcon={EnvelopeSimple}
                  autoFocus
                />
              </Field>
            )}
          />
          <Button
            type="submit"
            size="lg"
            className="w-full"
            loading={form.formState.isSubmitting}
          >
            Send reset code
          </Button>
        </AuthBody>
      </form>
      <BackToSignInFooter />
    </AuthCard>
  );
}

type EmailCodeValues = z.infer<typeof EmailCodeSchema>;

/**
 * Figma "Auth B — Forgot password — sent" (214:23201), plus the code field:
 * the reset code is entered here.
 */
export function ForgotPasswordSentPage() {
  const navigate = useNavigate();
  const { signIn } = useSignIn();
  const form = useForm<EmailCodeValues>({
    resolver: zodResolver(EmailCodeSchema),
    defaultValues: { code: '' },
  });

  const onSubmit = form.handleSubmit(async ({ code }) => {
    const { error } = await signIn.resetPasswordEmailCode.verifyCode({ code });
    if (error) {
      const { code: errorCode, message } = describeClerkError(error);
      if (errorCode === 'verification_expired') {
        await navigate('/reset-password/expired');
        return;
      }
      form.setError('code', {
        message: errorCode === 'form_code_incorrect' ? 'That code isn’t right' : message,
      });
      return;
    }
    await navigate('/reset-password');
  });

  async function resend() {
    const { error } = await signIn.resetPasswordEmailCode.sendCode();
    if (error) toast.error(describeClerkError(error).message);
    else toast.success('New code sent');
  }

  return (
    <AuthCard>
      <AuthHeader
        icon={{ icon: EnvelopeSimple }}
        title="Check your inbox"
        subtitle="If that address has an account, a six-digit code to choose a new password is on its way. It only works in this browser."
      />
      <form onSubmit={onSubmit} noValidate>
        <AuthBody>
          <Controller
            name="code"
            control={form.control}
            render={({ field, fieldState }) => (
              <CodeField
                value={field.value}
                onChange={field.onChange}
                onComplete={() => void onSubmit()}
                error={fieldState.error?.message}
                autoFocus
              />
            )}
          />
          <Button
            type="submit"
            size="lg"
            className="w-full"
            loading={form.formState.isSubmitting}
          >
            Continue
          </Button>
          <MailShortcuts />
          <BackToSignInButton variant="ghost" />
        </AuthBody>
      </form>
      <AuthFooter>
        Didn’t get it?
        <Link asChild>
          <button type="button" onClick={() => void resend()}>
            Resend code
          </button>
        </Link>
      </AuthFooter>
    </AuthCard>
  );
}

/** Figma "Auth B — Reset password" (214:23243). */
export function ResetPasswordPage() {
  const navigate = useNavigate();
  const { signIn } = useSignIn();
  const form = useForm<ResetPasswordValues>({
    resolver: zodResolver(ResetPasswordSchema),
    defaultValues: { password: '', confirm: '' },
  });

  const onSubmit = form.handleSubmit(async ({ password }) => {
    const { error } = await signIn.resetPasswordEmailCode.submitPassword({
      password,
      signOutOfOtherSessions: true,
    });
    if (error) {
      form.setError(
        'password',
        { message: describeClerkError(error).message },
        { shouldFocus: true },
      );
      return;
    }
    if (signIn.status !== 'complete') {
      toast.success('Password changed. Sign in with your new password.');
      await navigate('/sign-in', { replace: true });
      return;
    }
    toast.success('Password changed');
    await signIn.finalize({ navigate: () => navigate('/', { replace: true }) });
  });

  // Only reachable after a verified reset code. (Once submitted, the status
  // moves on while we finalize - don't bounce then.)
  const { isSubmitting, isSubmitSuccessful } = form.formState;
  if (signIn.status !== 'needs_new_password' && !isSubmitting && !isSubmitSuccessful) {
    return <Navigate to="/forgot-password" replace />;
  }

  return (
    <AuthCard>
      <AuthHeader
        icon={{ icon: LockSimple }}
        title="Choose a new password"
        subtitle="You’ll use it the next time you sign in"
      />
      <form onSubmit={onSubmit} noValidate>
        <AuthBody>
          <Controller
            name="password"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field label="New password" error={fieldState.error?.message}>
                <PasswordInput
                  {...field}
                  reveal
                  autoComplete="new-password"
                  placeholder="Choose a new password"
                  autoFocus
                />
              </Field>
            )}
          />
          <Controller
            name="confirm"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field label="Confirm new password" error={fieldState.error?.message}>
                <PasswordInput
                  {...field}
                  autoComplete="new-password"
                  placeholder="Repeat your new password"
                />
              </Field>
            )}
          />
          <Button
            type="submit"
            size="lg"
            className="w-full"
            loading={form.formState.isSubmitting}
          >
            Change password
          </Button>
        </AuthBody>
      </form>
    </AuthCard>
  );
}
